import React, { useEffect, useRef, useState } from 'react';
import { theme, alpha } from '../theme';
import { useResponsive } from '../hooks/useResponsive';
import { useAuth } from '../contexts/AuthContext';
import { useJoinApplicationThread } from '../hooks/useJoinApplicationThread';

interface Props {
  isOpen: boolean;
  matchId: string;
  applicationId: string;
  applicationStatus: string;
  otherPartyLabel: string;
  initialApplicationMessage: string;
  chatRole?: 'applicant' | 'organizer';
  onClose: () => void;
}

const MAX_MESSAGES = 25;

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

const JoinApplicationThreadModal: React.FC<Props> = ({
  isOpen,
  matchId,
  applicationId,
  applicationStatus,
  otherPartyLabel,
  initialApplicationMessage,
  chatRole = 'applicant',
  onClose,
}) => {
  const { isMobile } = useResponsive();
  const { user } = useAuth();
  const { messages, loading, sending, send } = useJoinApplicationThread(
    matchId,
    applicationId,
    isOpen
  );
  const [draft, setDraft] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const canSend = applicationStatus === 'PENDING' && messages.length < MAX_MESSAGES;

  useEffect(() => {
    if (isOpen) setDraft('');
  }, [isOpen]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, loading]);

  if (!isOpen) return null;

  const submit = async () => {
    setLocalError(null);
    const t = draft.trim();
    if (!t) return;
    if (!canSend) return;
    const res = await send(t);
    if (res.error) {
      setLocalError(res.error);
    } else {
      setDraft('');
    }
  };

  const palette = {
    wall: theme.colors.background,
    headerBg: theme.colors.surface,
    bubbleIn: theme.colors.surface,
    bubbleOut: alpha('primary', '60'),
    bubbleText: theme.colors.text,
    metaMuted: theme.colors.textSecondary,
    composerBg: theme.colors.surface,
    inputFill: theme.colors.background,
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        zIndex: 2200,
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: palette.wall,
          width: '100%',
          maxWidth: 520,
          height: isMobile ? '92vh' : '80vh',
          borderRadius: isMobile ? '20px 20px 0 0' : 18,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: `1px solid ${theme.colors.border}`,
        }}
      >
        {/* Header */}
        <div
          style={{
            background: palette.headerBg,
            padding: '14px 16px',
            borderBottom: `1px solid ${theme.colors.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 19,
              background: alpha('primary', '22'),
              color: theme.colors.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontFamily: theme.fonts.heading,
              fontSize: 14,
            }}
          >
            {(otherPartyLabel || '?').slice(0, 1).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                color: theme.colors.text,
                fontWeight: 700,
                fontSize: 15,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {otherPartyLabel}
            </div>
            <div style={{ fontSize: 11, color: palette.metaMuted }}>
              {applicationStatus === 'PENDING'
                ? 'Postulación pendiente'
                : applicationStatus === 'ACCEPTED'
                  ? 'Postulación aceptada (lectura)'
                  : applicationStatus === 'REJECTED'
                    ? 'Postulación rechazada (lectura)'
                    : applicationStatus}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              background: 'transparent',
              border: 'none',
              color: theme.colors.textSecondary,
              fontSize: 22,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 14,
            background: palette.wall,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {/* Initial application message as first bubble */}
          {initialApplicationMessage && (
            <Bubble
              text={initialApplicationMessage}
              isOwn={chatRole === 'applicant'}
              palette={palette}
              meta="postulación inicial"
            />
          )}

          {loading ? (
            <div style={{ textAlign: 'center', color: palette.metaMuted, padding: 20 }}>
              Cargando mensajes…
            </div>
          ) : (
            messages
              .filter((m) => m && typeof m.id === 'string')
              .map((m) => {
                const isOwn = m.authorId === user?.id;
                return (
                  <Bubble
                    key={m.id}
                    text={m.body ?? ''}
                    isOwn={isOwn}
                    palette={palette}
                    meta={formatTime(m.createdAt)}
                  />
                );
              })
          )}
        </div>

        {/* Composer */}
        <div
          style={{
            padding: 10,
            background: palette.composerBg,
            borderTop: `1px solid ${theme.colors.border}`,
          }}
        >
          {!canSend && applicationStatus !== 'PENDING' && (
            <div
              style={{
                fontSize: 12,
                color: palette.metaMuted,
                textAlign: 'center',
                marginBottom: 6,
              }}
            >
              La conversación está cerrada (solo lectura).
            </div>
          )}
          {messages.length >= MAX_MESSAGES && (
            <div
              style={{
                fontSize: 12,
                color: theme.colors.warning,
                textAlign: 'center',
                marginBottom: 6,
              }}
            >
              Límite de {MAX_MESSAGES} mensajes alcanzado.
            </div>
          )}
          {localError && (
            <div
              style={{
                fontSize: 12,
                color: theme.colors.danger,
                textAlign: 'center',
                marginBottom: 6,
              }}
            >
              {localError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void submit();
                }
              }}
              placeholder={canSend ? 'Escribí un mensaje…' : 'Conversación cerrada'}
              disabled={!canSend}
              style={{
                flex: 1,
                padding: '10px 12px',
                background: palette.inputFill,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: 999,
                color: theme.colors.text,
                fontSize: 14,
                outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={submit}
              disabled={!canSend || sending || !draft.trim()}
              style={{
                padding: '0 18px',
                background:
                  !canSend || sending || !draft.trim()
                    ? theme.colors.border
                    : theme.colors.primary,
                color: theme.colors.background,
                border: 'none',
                borderRadius: 999,
                fontWeight: 800,
                cursor: !canSend || sending || !draft.trim() ? 'not-allowed' : 'pointer',
                fontFamily: theme.fonts.heading,
              }}
            >
              {sending ? '…' : 'Enviar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Bubble: React.FC<{
  text: string;
  isOwn: boolean;
  palette: {
    bubbleIn: string;
    bubbleOut: string;
    bubbleText: string;
    metaMuted: string;
  };
  meta?: string;
}> = ({ text, isOwn, palette, meta }) => (
  <div
    style={{
      alignSelf: isOwn ? 'flex-end' : 'flex-start',
      maxWidth: '78%',
      background: isOwn ? palette.bubbleOut : palette.bubbleIn,
      color: palette.bubbleText,
      padding: '8px 12px',
      borderRadius: isOwn ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
      boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ fontSize: 14, lineHeight: '20px', whiteSpace: 'pre-wrap' }}>{text}</div>
    {meta && (
      <div
        style={{
          fontSize: 10,
          color: palette.metaMuted,
          marginTop: 2,
          textAlign: 'right',
        }}
      >
        {meta}
      </div>
    )}
  </div>
);

export default JoinApplicationThreadModal;
